import React, { useEffect, useState } from "react";
import { compose, withActions, withAppContext } from "containers/HOC";
import { Panel, PanelBody } from "components/Panel";
import { Card } from "components/CreditCards";
import FarmList from "./FarmList";
import BulkPayFarmList from "containers/Dashboard/FarmList";
import ActionLink from "components/ActionLink";
import Button from "components/Button/Button";
import { getNonRenewals, getRenewals, deleteCard, deleteAutoRenew, setCardForFarms, getMyOrgBalance } from "./_actions";
import { useModal } from "common/effects";
import CreditCardModal from "./CreditCardModal";
import AddCardModal from "./AddCardModal";
import UpdateCardExpiryModal from "./UpdateCardExpiryModal";
import Loader from "components/Loader";
import moment from "moment";
import { useSelector } from "react-redux";
import BalanceSummary from "components/BalanceSummary";
import { useConfirm } from "common/hooks";
import TileBodyMessage from "components/TileBodyMessage";
import * as icons from "common/icons";

const MyOrgRenewals = ({ getRenewals, deleteCard, deleteAutoRenew, setCardForFarms, getNonRenewals, authContext, getMyOrgBalance }) => {
    const [renewals, setRenewals] = useState([]);
    const [farms, setFarms] = useState([]);
    const [selectedCreditCardFarmIds, setSelectedCreditCardFarmIds] = React.useState([]);
    const [selectedInvoiceFarmIds, setSelectedInvoiceFarmIds] = React.useState([]);
    const [selectedNoRenewalFarmIds, setSelectedNoRenewalFarmIds] = React.useState([]);
    const [selectedColleagueFarmIds, setSelectedColleagueFarmIds] = React.useState([]);
    const [changeList, setChangeList] = React.useState([]);
    const { openModal, getModal } = useModal();
    const [loading, setLoading] = React.useState(true);
    const balance = useSelector((state) => state.myOrg.balance);
    const confirm = useConfirm();
    const addCardModal = getModal("AddCardModal");
    const creditCardModal = getModal("CreditCardModal");
    const expiryModal = getModal("UpdateCardExpiryModal");

    const fetchFarmsAndRenewals = () => {
        setLoading(true);
        getRenewals()
            .then((r) => {
                setRenewals(r);
                setLoading(false);
            })
            .catch((error) => console.log(error));
        getNonRenewals(authContext.accountId)
            .then((r) => {
                setFarms(r);
            })
            .catch((error) => console.log(error));
    };

    useEffect(() => {
        getMyOrgBalance(authContext.accountId);
    }, [authContext.accountId, getMyOrgBalance]);

    useEffect(() => {
        getRenewals()
            .then((r) => {
                setRenewals(r);
                setLoading(false);
            })
            .catch((error) => console.log(error));
    }, [getRenewals]);

    useEffect(() => {
        getNonRenewals(authContext.accountId)
            .then((r) => {
                setFarms(r);
            })
            .catch((error) => console.log(error));
    }, [authContext.accountId, getNonRenewals]);

    const actionDeleteCard = async (id) => {
        await deleteCard(id);
        fetchFarmsAndRenewals();
    };

    const deleteCardConfirm = (card) => {
        const extMessage = renewals && renewals.length > 1 && card.farms && card.farms.length > 0 ? `Farms associated with this payment method will be moved to your other payment method.` : renewals && renewals.length === 1 && card.farms && card.farms.length > 0 ? "Farms associated with this payment method will no longer auto renew." : "";
        confirm(`Are you sure you want to delete this payment method? ${extMessage}`, () => actionDeleteCard(card.paymentToken.id));
    };

    const changeCard = (list) => {
        setChangeList(list);
        const modalProps = {
            cards: renewals.filter((r) => !r.paymentToken.isExpired).map((r) => r.paymentToken),
        };
        openModal("CreditCardModal", modalProps);
    };

    const setNewCard = async (cardId) => {
        await setCardForFarms(changeList, cardId);
        fetchFarmsAndRenewals();
        setChangeList([]);
        setSelectedCreditCardFarmIds([]);
        setSelectedInvoiceFarmIds([]);
        setSelectedNoRenewalFarmIds([]);
    };

    const removeFarms = async (list) => {
        await deleteAutoRenew(list);
        fetchFarmsAndRenewals();
        setChangeList([]);
        setSelectedCreditCardFarmIds([]);
        setSelectedInvoiceFarmIds([]);
        setSelectedNoRenewalFarmIds([]);
    };

    const removeFarmsConfirm = (list) => {
        confirm(`Are you sure you want to delete auto renew for the ${list.length > 1 ? list.length + " selected farms?" : "selected farm?"}`, () => removeFarms(list));
    };

    const addCard = () => {
        const modalProps = {};
        const onClose = () => {
            fetchFarmsAndRenewals();
        };
        openModal("AddCardModal", modalProps, onClose);
    };

    const updateExpiry = (d) => {
        const modalProps = { card: d.paymentToken };
        const onClose = () => {
            fetchFarmsAndRenewals();
        };
        openModal("UpdateCardExpiryModal", modalProps, onClose);
    };

    if (loading) return <Loader message="Loading..." />;

    if (!renewals || renewals.length === 0) {
        return (
            <>
                {addCardModal && <AddCardModal {...addCardModal.modalProps} />}
                <div className="Tile-body-message">
                    <span className="u-text-lg u-textBold u-mb-md">You do not have any saved payment methods</span>
                    <Button onClick={addCard} secondary id="add-card-button">
                        <i className="IconLink--arrow-plus u-mr-xs" />
                        Add new card
                    </Button>
                </div>
            </>
        );
    }

    const farmCount = renewals.reduce((t, r) => (t += r.farms.length), 0);
    const cardRewewals = renewals.filter((r) => r.paymentToken?.paymentMethod === "CreditCard");
    const invoiceRewewals = renewals.filter((r) => r.paymentToken?.paymentMethod === "Invoice");
    const expiredFarms = farms.filter((f) => f.expired);
    const farmsWithOtherOrgs = [...farms.filter((f) => f.paymentTokenId && !f.expired && f.paidByAccountId !== authContext.accountId)];
    const farmsWithColleagues = [...farms.filter((f) => f.paymentTokenId && !f.expired && f.paidByAccountId === authContext.accountId)];
    const farmsWithNoRenewals = [...farms.filter((f) => !f.paymentTokenId && !f.expired)];

    const ActionAlert = ({ text, children }) => {
        return (
            <div className={`u-mt-0 u-flexJustifyBetween Alert Alert--info u-flexStart`}>
                <div className="u-flex u-flexSelfStart">
                    <i className={"icon icon-alert"} />
                    <div style={{ marginTop: "3px" }}>{text}</div>
                </div>
                {children}
            </div>
        );
    };

    return (
        <>
            {addCardModal && <AddCardModal {...addCardModal.modalProps} />}
            {creditCardModal && <CreditCardModal {...creditCardModal.modalProps} submitCard={setNewCard} />}
            {expiryModal && <UpdateCardExpiryModal {...expiryModal.modalProps} />}
            <div>
                <Panel loading={!renewals} className="u-mb-xl" title="Credit card">
                    {cardRewewals.length > 0 && (
                        <ActionAlert text="Farms set up to automatically renew subscription by credit card.">
                            <div className="u-pl-md u-pr-md u-flex u-flexJustifyEnd">
                                <div>
                                    <ul className="u-flex">
                                        <li>
                                            <ActionLink onClick={() => addCard()} className="IconLink--arrow-plus u-mr-sm" id="add-card">
                                                Add new card
                                            </ActionLink>
                                        </li>
                                        <li>
                                            <ActionLink disabled={!renewals || !selectedCreditCardFarmIds || selectedCreditCardFarmIds.length === 0} onClick={() => changeCard(selectedCreditCardFarmIds)} className="IconLink--copy" id="change-card">
                                                Change payment method
                                            </ActionLink>
                                        </li>
                                        <li className="u-pl-sm">
                                            <ActionLink onClick={() => removeFarmsConfirm(selectedCreditCardFarmIds)} disabled={!selectedCreditCardFarmIds || selectedCreditCardFarmIds.length === 0 || farmCount === 0} className="IconLink--trash" id="remove-card">
                                                Remove auto renew
                                            </ActionLink>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </ActionAlert>
                    )}
                    {cardRewewals.map((r, i) => {
                        const date = moment(r.paymentToken.expiresOn);
                        const expDate = date.isValid() ? date.format("MM/YY") : "-";
                        return (
                            <PanelBody key={i}>
                                <div className="u-flex u-flexEnd u-mt-md">
                                    <Card card={r.paymentToken} />
                                    <div className="u-flex u-flexJustifyEnd u-mt-0 u-mb-md">
                                        <ActionLink name={`${r.paymentToken.cardIdentifier}${expDate.replace("/", "")}`} id={`delete_card_${r.paymentToken.id}`} onClick={() => deleteCardConfirm(r)} className="IconLink--trash u-mr-sm">
                                            Delete card
                                        </ActionLink>
                                        <ActionLink name={`${r.paymentToken.cardIdentifier}${expDate.replace("/", "")}`} id={`delete_card_${r.paymentToken.id}`} onClick={() => updateExpiry(r)} className="IconLink--edit">
                                            Update expiry
                                        </ActionLink>
                                    </div>
                                </div>
                                <FarmList farms={r.farms} tokenId={r.paymentToken.id} selectedFarmIds={selectedCreditCardFarmIds} setSelectedFarmIds={setSelectedCreditCardFarmIds} message="Farms auto renewing using this card" pageSize={50} showFarmLink={true} />
                            </PanelBody>
                        );
                    })}
                    {cardRewewals.length === 0 && (
                        <div>
                            <div className="u-pb-sm">
                                <TileBodyMessage>
                                    <img src={icons.add} className="u-p-5" alt="Plus" />
                                    <p className="lead">You have no saved credit cards</p>
                                    <ActionLink onClick={addCard} className="Button Button--secondary Button--lg u-mt-md">
                                        Add new card
                                    </ActionLink>
                                </TileBodyMessage>
                            </div>
                        </div>
                    )}
                </Panel>

                {farmsWithColleagues.length > 0 && (
                    <Panel loading={!renewals} className="u-mb-xl" title="Colleague credit card renewals">
                        <ActionAlert text="Farms set up to automatically renew with your organisation that are paid with a colleagues credit card.">
                            <div className="u-pl-md u-pr-md">
                                <div>
                                    <ul className="u-flex">
                                        <li>
                                            <ActionLink disabled={!renewals || !selectedColleagueFarmIds || selectedColleagueFarmIds.length === 0} onClick={() => changeCard(selectedColleagueFarmIds)} className="IconLink--copy" id="add-fertiliser">
                                                Change payment method
                                            </ActionLink>
                                        </li>
                                        <li className="u-pl-sm">
                                            <ActionLink onClick={() => removeFarmsConfirm(selectedColleagueFarmIds)} disabled={!selectedColleagueFarmIds || selectedColleagueFarmIds.length === 0 || farmCount === 0} className="IconLink--trash" id="add-fertiliser">
                                                Remove auto renew
                                            </ActionLink>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </ActionAlert>

                        <PanelBody>
                            <FarmList farms={farmsWithColleagues} tokenId="farmsWithColleagues" selectedFarmIds={selectedColleagueFarmIds} setSelectedFarmIds={setSelectedColleagueFarmIds} message="Farms that renew with colleagues" pageSize={50} showPaidBy={true} />
                        </PanelBody>
                    </Panel>
                )}

                {invoiceRewewals.length > 0 && (
                    <Panel loading={!renewals} className="u-mb-xl" title="Invoice">
                        <ActionAlert text="Farms set up to automatically renew subscription by invoice. There must be sufficient available balance at renewal.">
                            <div className="u-pl-md u-pr-md">
                                <div>
                                    <ul className="u-flex">
                                        <li>
                                            <ActionLink disabled={!renewals || !selectedInvoiceFarmIds || selectedInvoiceFarmIds.length === 0} onClick={() => changeCard(selectedInvoiceFarmIds)} className="IconLink--copy" id="add-fertiliser">
                                                Change payment method
                                            </ActionLink>
                                        </li>
                                        <li className="u-pl-sm">
                                            <ActionLink onClick={() => removeFarmsConfirm(selectedInvoiceFarmIds)} disabled={!selectedInvoiceFarmIds || selectedInvoiceFarmIds.length === 0 || farmCount === 0} className="IconLink--trash" id="add-fertiliser">
                                                Remove auto renew
                                            </ActionLink>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </ActionAlert>
                        <div className="u-ml-md">
                            <div>{authContext.isAdmin && balance && <BalanceSummary balance={balance} authContext={authContext} accountId={authContext.accountId} />}</div>
                        </div>

                        {invoiceRewewals.map((r, i) => {
                            return (
                                <PanelBody key={i}>
                                    <div className="u-flex u-flexEnd u-mt-md">
                                        <Card card={r.paymentToken} />
                                        <div className="u-pb-md">
                                            {false && (
                                                <Button name={`${r.paymentToken.cardIdentifier}`} id={`delete_card_${r.paymentToken.id}`} onClick={() => deleteCardConfirm(r)} secondary>
                                                    Delete payment method
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    <FarmList farms={r.farms} tokenId={r.paymentToken.id} selectedFarmIds={selectedInvoiceFarmIds} setSelectedFarmIds={setSelectedInvoiceFarmIds} message="Farms auto renewing by invoice" pageSize={50} showFarmLink={true} />
                                </PanelBody>
                            );
                        })}
                    </Panel>
                )}

                {(farmsWithNoRenewals.length > 0 || farmsWithOtherOrgs.length > 0) && (
                    <Panel loading={!farms} className="u-mb-xl" title="Farms not auto renewing with this organisation">
                        <ActionAlert text="Farms that your organisation has access to that are not set up to auto renew from your credit card or invoice.">
                            <div className="u-pl-md u-pr-md u-flex u-flexJustifyEnd">
                                <div>
                                    <ul className="u-flex">
                                        <li>
                                            <ActionLink disabled={!renewals || !selectedNoRenewalFarmIds || selectedNoRenewalFarmIds.length === 0} onClick={() => changeCard(selectedNoRenewalFarmIds)} className="IconLink--copy" id="add-fertiliser">
                                                Add payment method
                                            </ActionLink>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </ActionAlert>

                        <PanelBody>
                            {farmsWithNoRenewals.length > 0 && (
                                <div>
                                    <FarmList farms={farmsWithNoRenewals} tokenId="farmsWithNoRenewals" selectedFarmIds={selectedNoRenewalFarmIds} setSelectedFarmIds={setSelectedNoRenewalFarmIds} message="Farms without auto renewal" pageSize={50} showPaidBy={false} />
                                </div>
                            )}
                            {farmsWithOtherOrgs.length > 0 && (
                                <div className="u-mt-md">
                                    <FarmList farms={farmsWithOtherOrgs} tokenId="farmsWithOtherOrgs" selectedFarmIds={selectedNoRenewalFarmIds} setSelectedFarmIds={setSelectedNoRenewalFarmIds} message="Farms who renew with others" pageSize={50} showPaidBy={true} />
                                </div>
                            )}
                        </PanelBody>
                    </Panel>
                )}

                {expiredFarms.length > 0 && (
                    <Panel loading={!farms} className="u-mb-xl" title="Expired farms">
                        <ActionAlert type={"info"} text="Farms which have expired." />
                        <Panel className="u-mb-xl">
                            <PanelBody>
                                <BulkPayFarmList farms={expiredFarms} reload={fetchFarmsAndRenewals} pageSize={50} hideMyFarm={true} />
                            </PanelBody>
                        </Panel>
                    </Panel>
                )}
            </div>
        </>
    );
};

export default compose(withAppContext, withActions({ getMyOrgBalance, getRenewals, deleteCard, deleteAutoRenew, setCardForFarms, getNonRenewals }))(MyOrgRenewals);
